// HeyCatch Analytics — strictly consent-gated, lazy-loaded.
//
// Reinstated 2026-10-28 after prior removal (commit 3ff70b7). Mirrors the
// architecture of commit bf93be3 but with four hardening changes:
//
//   1. Project key is read from REACT_APP_HEYCATCH_PROJECT_KEY (env), never
//      hardcoded in source — rotate via the secrets card, no code change.
//
//   2. The @heycatch/sdk module is behind a dynamic `import()`. The main
//      bundle contains no HeyCatch code; the vendor module (and its
//      network dependencies) is downloaded ONLY when analytics consent
//      becomes true, matching the "do not load before consent" directive.
//
//   3. Listens to the modern `zy:analytics-consent-changed` custom event
//      dispatched by CookieSettings.jsx, in addition to the legacy
//      cross-tab `storage` event. Toggles react synchronously same-tab.
//
//   4. `beforeSend` drops events on authenticated routes so no product-
//      side activity ever reaches the vendor. Autocapture stays SDK-owned.
//
// Co-exists with GTM, GA4, PostHog and FirstPromoter — none duplicated.

const CONSENT_KEY = "zy_cookie_prefs_v1";
const PUBLIC_ROUTES = new Set([
  "/", "/pricing", "/modules", "/assistants", "/affiliate", "/website",
  "/features", "/about", "/contact", "/blog", "/faq",
  "/vs/zynthoro-vs-odoo", "/vs/zynthoro-vs-netsuite",
]);

let analytics = null;           // SDK module instance once dynamically loaded.
let sdkLoadPromise = null;      // In-flight import(); cached to prevent double loads.
let currentUser = null;
let identitySnapshot = "";
let allowed = false;
let configured = false;

function hasConsent() {
  if (typeof window === "undefined") return false;
  if (window.__zyAnalyticsDeniedForPage === true) return false;
  try {
    const prefs = JSON.parse(window.localStorage.getItem(CONSENT_KEY) || "null");
    return !!prefs && prefs.analytics === true;
  } catch {
    return false;
  }
}

function filterHeycatchEvent(event) {
  if (!event || !hasConsent()) return null;
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const publicPage =
    PUBLIC_ROUTES.has(path) ||
    path.startsWith("/blog/") ||
    path.startsWith("/legal/") ||
    path.startsWith("/modules/") ||
    path.startsWith("/vs/");
  if (!publicPage && event.event !== "$identify") return null;
  return event;
}

async function ensureSdkInitialized(projectKey) {
  if (analytics) return analytics;
  if (!sdkLoadPromise) {
    sdkLoadPromise = import(/* webpackChunkName: "heycatch" */ "@heycatch/sdk")
      .then((mod) => {
        mod.analytics.init({
          projectKey,
          install: { framework: "react", frameworkVersion: "19", agent: "emergent" },
          optOutCapturingByDefault: true,
          maskAllText: true,
          maskAllElementAttributes: true,
          beforeSend: filterHeycatchEvent,
        });
        analytics = mod.analytics;
        return analytics;
      })
      .catch(() => {
        // Vendor outage or script-blocked by a privacy extension — stay
        // silent, never throw into the host app.
        sdkLoadPromise = null;
        return null;
      });
  }
  return sdkLoadPromise;
}

function identifyIfChanged() {
  if (!analytics || !allowed || !currentUser?.id) return;
  const properties = {
    email: currentUser.email,
    name: [currentUser.first_name, currentUser.last_name].filter(Boolean).join(" "),
    plan:
      currentUser.business_demo && currentUser.is_trial
        ? "Business Demo"
        : currentUser.subscription_plan,
  };
  const snapshot = JSON.stringify([currentUser.id, properties, currentUser.created_at]);
  if (snapshot === identitySnapshot) return;
  analytics.setIdentity(currentUser.id, properties, { signup_date: currentUser.created_at });
  identitySnapshot = snapshot;
}

export function setHeycatchUser(user) {
  if (!configured) return;
  currentUser = user && typeof user.id === "string" ? user : null;
  if (!currentUser) {
    if (analytics && allowed && identitySnapshot) analytics.resetIdentity();
    identitySnapshot = "";
    return;
  }
  identifyIfChanged();
}

export function resetHeycatchUser() {
  if (!configured) return;
  currentUser = null;
  identitySnapshot = "";
  if (analytics && allowed) analytics.resetIdentity();
}

export function applyHeycatchConsent(requested) {
  if (!configured) return;
  const next = requested === true && hasConsent();

  // If consent is false, make sure we are opted out (if the SDK already
  // loaded) and do NOT trigger an SDK download.
  if (!next) {
    if (allowed && analytics) {
      analytics.optOutCapturing();
      identitySnapshot = "";
    }
    allowed = false;
    return;
  }

  // Consent is true — load the SDK lazily (first time only) and opt in.
  allowed = true;
  const projectKey = process.env.REACT_APP_HEYCATCH_PROJECT_KEY;
  if (!projectKey) return;
  ensureSdkInitialized(projectKey).then((sdk) => {
    if (!sdk) return;
    // Consent may have flipped back to false while the SDK was loading.
    if (!hasConsent()) {
      sdk.optOutCapturing();
      allowed = false;
      return;
    }
    sdk.optInCapturing();
    identifyIfChanged();
  });
}

export function handleHeycatchStorageChange(event) {
  if (!configured) return;
  if (event.key === CONSENT_KEY || event.key === null) {
    applyHeycatchConsent(true);
  }
}

// Called once from src/index.js. Does NOT load the SDK; it only wires up
// the consent machinery. The SDK is dynamically imported inside
// `applyHeycatchConsent` the first time analytics consent becomes true.
export function initHeycatch() {
  if (configured) return true;
  if (!process.env.REACT_APP_HEYCATCH_PROJECT_KEY) {
    // No key configured → the entire integration stays dormant. We still
    // mark configured=true for the no-op early-return guards, but we
    // never return "ready" so the listeners stay unregistered.
    return false;
  }
  configured = true;
  // Propagate persisted consent on boot (if the visitor already opted in
  // on a prior visit, the SDK downloads now; otherwise nothing happens).
  applyHeycatchConsent(true);
  return true;
}
